// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  EmailAuthProvider,
  linkWithCredential,
  GoogleAuthProvider,
  signOut,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();
const googleProvider = new GoogleAuthProvider();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const createUserDocument = async (firebaseUser) => {
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const userDoc = await getDoc(userDocRef);
    
    // Only create if doesn't exist
    if (!userDoc.exists()) {
      await setDoc(userDocRef, {
        email: firebaseUser.email || null,
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || null,
        isAnonymous: !!firebaseUser.isAnonymous,
        createdAt: serverTimestamp(),
        roles: ['user'],
        cart: [],
        purchases: [],
        location: null,
        postalCode: '',
      });
    }
  };

  const ensureAnonymousUser = async () => {
    if (auth.currentUser) {
      await createUserDocument(auth.currentUser);
      setUser(auth.currentUser);
      return auth.currentUser;
    }

    const userCredential = await signInAnonymously(auth);
    await createUserDocument(userCredential.user);
    setUser(userCredential.user);
    return userCredential.user;
  };
  const upgradeAnonymousUser = async (email, password) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No active user session found');
    }
    if (!currentUser.isAnonymous) {
      throw new Error('This account already has sign-in credentials');
    }

    const credential = EmailAuthProvider.credential(email, password);
    const userCredential = await linkWithCredential(currentUser, credential);

    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email || email,
      isAnonymous: false,
      upgradedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });

    setUser(userCredential.user);
    return userCredential;
  };


  const register = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create Firestore user document
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email,
      displayName: '',
      photoURL: null,
      createdAt: serverTimestamp(),
      roles: ['user'],
      cart: [],
      purchases: [],
      location: null,
      postalCode: '',
    });
    
    // Redirect to intended page after registration
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
    
    return userCredential;
  };

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Create user document if it doesn't exist (for existing users)
    await createUserDocument(userCredential.user);
    
    // Redirect to intended page after login
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
    
    return userCredential;
  };

  const loginWithGoogle = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      
      // Create user document if it doesn't exist
      await createUserDocument(userCredential.user);
      
      // Redirect to intended page after login
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
      
      return userCredential;
    } catch (error) {
      // Re-throw the error to be handled in the component
      throw error;
    }
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, loading, register, login, loginWithGoogle, ensureAnonymousUser, upgradeAnonymousUser, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};